import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSetAtom, useStore } from 'jotai';
import { useCallback } from 'react';
import { getRequest, putRequest } from '../Services/HttpService';
import { isLoggedIn } from '../Services/UserService';
import { selectedVehiclesAtom, vehiclesAtom } from '../store/jotai/vehicles';
import { MapSettings } from '../components/UserData/types';
import { TMapType } from '../interfaces/map';
import {
  RideDetail,
  RideLayerSettingsDTO,
  RidesLayerSettings,
} from '../components/OnlineMapNew/RideHistoryLayer/types';
import {
  getMenuSettings,
  MenuSettingsValue,
  saveMenuSettings,
} from '../components/MainMenu/MainMenuService';

export { getMenuSettings };

export type NavigatorSettings = {
  preselectCustomerVehicles: number[];
  compactList: boolean;
  // hideInactiveVehicles: boolean;
  favoriteBluecoinsFromSetting: number[];
};

export type UserSettingsResponse = {
  navigator: NavigatorSettings;
};

export interface MapSettingsDTO {
  alarms: boolean;
  clusters: boolean;
  driverName: boolean;
  events: {
    enabled: boolean;
    additional: boolean;
    bluecoins: boolean;
    doors: boolean;
  };
  identification: boolean;
  licenceNumber: boolean;
  mapType: string;
  vehicleName: boolean;
  bluecoinName: boolean;
}

interface RidePlayerSettingsDTO {
  rideRenderType: string;
  showPause: boolean;
  speedLimits: {
    enabled: boolean;
    limits: number[];
  };
  playerSpeed: number;
}

interface SpeedLimitsDTO {
  '60': string;
  '90': string;
  '130': string;
  '>=130': string;
}

interface NavigatorSettingsDTO {
  preselectCustomerVehicles: number[];
  compactList: boolean;
  // hideInactiveVehicles: boolean;
  favoriteBluecoinsFromSetting: number[];
}

type MenuItemsDTO = {
  enabled: number;
  orderfld: number;
  menu_item_id: number;
};

export type UserSettingsDTO = {
  date_range_picker: {
    interval: string;
    dateLow: string;
    dateHigh: string;
  };
  map_settings: MapSettingsDTO;
  ride_player: RidePlayerSettingsDTO;
  speed_limits: SpeedLimitsDTO;
  navigator: NavigatorSettingsDTO;
  menu_items: MenuItemsDTO[];
  menu_items_left: MenuItemsDTO[];
  // refueling: response will be ignored for now
  // period_lock_look_back: response will be ignored for now
  // adm_gui_settings: response will be ignored for now
  // mobile_adm_gui_settings: response will be ignored for now
  // selected_customers response will be ignored for now
  // list_config_* response will be ignored for now
  // report_config_* response will be ignored for now
  // auto_report_config_* response will be ignored for now
  ride_history_on_map: null | {
    rideDetail?: RideDetail;
    identification: {
      enabled: boolean;
      showLicenseNumber: boolean;
      showDriverName: boolean;
      showVehicleName: boolean;
    };
  };
};

export const getUserSettings = async (): Promise<any | null> => {
  const response = await getRequest<UserSettingsDTO | null>('/v1/user-settings');
  return response ?? null;
};

const saveUserSettings = async (
  key: string,
  settings: NavigatorSettings | MapSettings | RidesLayerSettings
): Promise<any> => {
  const response = await putRequest<
    NavigatorSettings | MapSettings | RidesLayerSettings,
    NavigatorSettingsDTO | MapSettingsDTO | RideLayerSettingsDTO
  >(`/v2/user-settings/${key}`, settings);
  return response ?? null;
};

export const MENU_SETTINGS_QUERY_KEY = ['userSettings', 'menu'];

const sameCustomerIds = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const setA = new Set(a.map(Number));
  return b.every((id) => setA.has(Number(id)));
};

export default function useUserSettings(): {
  navigator: NavigatorSettingsDTO | null;
  mapSettings: MapSettingsDTO | null;
  rideLayerSettings: RideLayerSettingsDTO;
  menuOpen: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  isError: boolean;
  isMenuLoading: boolean;
  saveNavigatorSettings: UseMutationResult<NavigatorSettings, Error, NavigatorSettings>;
  saveMapSettings: UseMutationResult<MapSettings, Error, MapSettings>;
  saveRideLayerSettings: UseMutationResult<RidesLayerSettings, Error, RidesLayerSettings>;
  saveMenuOpen: UseMutationResult<MenuSettingsValue | null, Error, boolean>;
  updateClusters: (enableClusters: boolean) => void;
  updateMapType: (mapType: TMapType) => void;
  data: UserSettingsDTO | null | undefined;
  getMapType: () => TMapType;
} {
  const queryClient = useQueryClient();
  const store = useStore();
  const setSelectedVehicles = useSetAtom(selectedVehiclesAtom);

  const updateSelectedVehiclesByCustomerIds = useCallback(
    (preselectedCustomerIds: number[]) => {
      const vehicles = store.get(vehiclesAtom);
      const selectedVehiclesByCustomerIds = Object.values(vehicles)
        .filter((vehicle) => preselectedCustomerIds.includes(vehicle.customerId))
        .map((vehicle) => vehicle.id);
      setSelectedVehicles(selectedVehiclesByCustomerIds);
    },
    [store, setSelectedVehicles]
  );

  const { data, isSuccess, isLoading, isError } = useQuery({
    queryKey: ['userSettings'],
    queryFn: getUserSettings,
    staleTime: Infinity, // Data is considered fresh forever, won't refetch
    gcTime: Infinity, // Keep in cache forever, won't be garbage collected
    refetchOnMount: false, // Don't refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    // refetchOnReconnect: false, // Don't refetch when network reconnects
    enabled: isLoggedIn(),
  });

  const { data: menuSettings, isLoading: isMenuLoading } = useQuery({
    queryKey: MENU_SETTINGS_QUERY_KEY,
    queryFn: getMenuSettings,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: isLoggedIn(),
  });

  const saveMenuOpenMutation = useMutation({
    mutationKey: MENU_SETTINGS_QUERY_KEY,
    mutationFn: (open: boolean) => saveMenuSettings(open),
    onSuccess: (menuSettingsValue) => {
      if (menuSettingsValue) {
        queryClient.setQueryData(MENU_SETTINGS_QUERY_KEY, menuSettingsValue);
      }
    },
    onError: (error: Error) => {
      console.error('saveMenuOpenMutation.onError called with:', error);
    },
  });

  const saveNavigatorSettingsMutation = useMutation({
    mutationKey: ['userSettings', 'navigator'],
    mutationFn: (navigatorSettings: NavigatorSettings) => {
      return saveUserSettings('navigator', navigatorSettings);
    },
    onMutate: (newNavigatorData) => {
      const preselectedCustomerIds = newNavigatorData?.preselectCustomerVehicles ?? [];
      const compactValue = newNavigatorData?.compactList ?? false;
      // const hideInactive = newNavigatorData?.hideInactiveVehicles ?? false;
      const favoriteBluecoins = newNavigatorData?.favoriteBluecoinsFromSetting ?? [];

      const previousPreselectedCustomerIds =
        queryClient.getQueryData<UserSettingsDTO | undefined>(['userSettings'])?.navigator
          ?.preselectCustomerVehicles ?? [];

      queryClient.setQueryData<UserSettingsDTO | undefined>(['userSettings'], (oldState) =>
        oldState
          ? {
              ...oldState,
              navigator: {
                preselectCustomerVehicles: preselectedCustomerIds,
                compactList: compactValue,
                // hideInactiveVehicles: hideInactive,
                favoriteBluecoinsFromSetting: favoriteBluecoins,
              },
            }
          : oldState
      );

      // Applying the preselect REPLACES the whole map selection, so it may only run when the
      // preselect itself changed. The settings modal saves all navigator settings on Confirm,
      // so doing this unconditionally wiped the user's manual vehicle selection whenever they
      // confirmed after changing something else (compact list, favourite bluecoins, clustering).
      if (!sameCustomerIds(previousPreselectedCustomerIds, preselectedCustomerIds)) {
        updateSelectedVehiclesByCustomerIds(preselectedCustomerIds);
      }
    },
    onError: (error: Error) => {
      console.error('saveNavigatorMutation.onError called with:', error);
    },
  });

  const saveMapSettingsMutation = useMutation({
    mutationKey: ['userSettings', 'mapSettings'],
    mutationFn: (mapSettings: MapSettings) => {
      return saveUserSettings('map_settings', mapSettings);
    },
    onMutate: (newMapSettingsData) => {
      queryClient.setQueryData<UserSettingsDTO | undefined>(['userSettings'], (oldState) =>
        oldState
          ? {
              ...oldState,
              map_settings: newMapSettingsData,
            }
          : oldState
      );
    },
    onError: (error: Error) => {
      console.error('saveNavigatorMutation.onError called with:', error);
    },
  });

  const saveRideLayerSettingsMutation = useMutation({
    mutationKey: ['userSettings', 'rideLayerSettings'],
    mutationFn: (rideLayerSettings: RidesLayerSettings) => {
      return saveUserSettings('ride_history_on_map', rideLayerSettings);
    },
    onMutate: (rideLayerSettings) => {
      queryClient.setQueryData<UserSettingsDTO | undefined>(['userSettings'], (oldState) =>
        oldState
          ? {
              ...oldState,
              ride_history_on_map: rideLayerSettings,
            }
          : oldState
      );
    },
    onError: (error: Error) => {
      console.error('saveNavigatorMutation.onError called with:', error);
    },
  });

  const updateClusters = (enableClusters: boolean): void => {
    if (!data) {
      return;
    }
    saveMapSettingsMutation.mutate({
      ...data.map_settings,
      clusters: enableClusters,
    });
  };

  const getMapType = () => {
    if (!data) {
      return 'roadmap';
    }
    if (!data.map_settings) {
      return 'roadmap';
    }
    if (!data.map_settings?.mapType) {
      return 'roadmap';
    }
    switch (data.map_settings.mapType) {
      case 'hybrid':
        return 'hybrid';
      case 'roadmap':
        return 'roadmap';
      default:
        return 'roadmap';
    }
  };

  const updateMapType = (mapType: TMapType): void => {
    if (!data) {
      return;
    }
    saveMapSettingsMutation.mutate({
      ...data.map_settings,
      mapType,
    });
  };

  const navigator: NavigatorSettingsDTO | null = data?.navigator ?? null;
  const mapSettings: MapSettingsDTO | null = data?.map_settings ?? null;
  const rideLayerSettings: RideLayerSettingsDTO = data?.ride_history_on_map ?? {
    rideDetail: 'basic',
    identification: {
      enabled: true,
      showLicenseNumber: true,
      showDriverName: true,
      showVehicleName: true,
    },
  };
  const menuOpen = menuSettings?.open ?? false;

  return {
    isSuccess,
    isLoading,
    isError,
    isMenuLoading,
    navigator,
    mapSettings,
    rideLayerSettings,
    menuOpen,
    saveNavigatorSettings: saveNavigatorSettingsMutation,
    saveMapSettings: saveMapSettingsMutation,
    saveRideLayerSettings: saveRideLayerSettingsMutation,
    saveMenuOpen: saveMenuOpenMutation,
    updateClusters,
    updateMapType,
    getMapType,
    data,
  };
}
